export const queryKeys = {
  climateActions: {
    all: ["climateActions"] as const,
    lists: () => [...queryKeys.climateActions.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.climateActions.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.climateActions.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.climateActions.details(), id] as const,
    assignee: (ownerId: string, ownerType: string) =>
      [...queryKeys.climateActions.all, "assignee", { ownerId, ownerType }] as const,
    priority: (ownerId: string, ownerType: string) =>
      [...queryKeys.climateActions.all, "priority", { ownerId, ownerType }] as const,
    cachedSuggestions: (workspaceId: string, includeInventory?: boolean, applyPreFilter?: boolean) =>
      [
        ...queryKeys.climateActions.all,
        "cachedSuggestions",
        {
          workspaceId,
          ...(includeInventory !== undefined ? { includeInventory } : {}),
          ...(applyPreFilter !== undefined ? { applyPreFilter } : {}),
        },
      ] as const,
    activeRejectedSuggestions: (workspaceId: string) =>
      [...queryKeys.climateActions.all, "activeRejectedSuggestions", { workspaceId }] as const,
    suggestionPromptPreview: (workspaceId: string, includeInventory?: boolean, applyPreFilter?: boolean) =>
      [
        ...queryKeys.climateActions.all,
        "suggestionPromptPreview",
        {
          workspaceId,
          ...(includeInventory !== undefined ? { includeInventory } : {}),
          ...(applyPreFilter !== undefined ? { applyPreFilter } : {}),
        },
      ] as const,
    suggestionJob: (jobId: string) => [...queryKeys.climateActions.all, "suggestionJob", jobId] as const,
    latestSuggestionJob: (workspaceId: string) =>
      [...queryKeys.climateActions.all, "latestSuggestionJob", { workspaceId }] as const,
  },

  measureCatalog: {
    all: ["measureCatalog"] as const,
    list: (workspaceId: string) => [...queryKeys.measureCatalog.all, "list", { workspaceId }] as const,
  },

  workspaces: {
    all: ["workspaces"] as const,
    details: () => [...queryKeys.workspaces.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.workspaces.details(), id] as const,
    byUrl: (url: string) => [...queryKeys.workspaces.all, "byUrl", url] as const,
    membershipLists: () => [...queryKeys.workspaces.all, "membershipList"] as const,
    membershipList: (workspaceId: string) => [...queryKeys.workspaces.membershipLists(), workspaceId] as const,
    superuserList: ["superuser_workspaceList"] as const,
    superuserNameList: ["superuser_workspaceNameList"] as const,
    settings: (workspaceId: string) => [...queryKeys.workspaces.all, "settings", workspaceId] as const,
    populations: (workspaceId: string) => [...queryKeys.workspaces.all, "population", workspaceId] as const,
    population: (workspaceId: string, year: number) =>
      [...queryKeys.workspaces.populations(workspaceId), year] as const,
    usage: (workspaceId: string) => [...queryKeys.workspaces.all, "usage", workspaceId] as const,
    usageList: ["workspaces", "usageList"] as const,
    storageReconciliation: ["workspaces", "storageReconciliation"] as const,
    objectMetadataCapture: ["workspaces", "objectMetadataCapture"] as const,
    objectBackfill: ["workspaces", "objectBackfill"] as const,
    duplicationProgress: (progressId: string) =>
      [...queryKeys.workspaces.all, "duplicationProgress", progressId] as const,
  },

  workspaceExports: {
    all: ["workspaceExports"] as const,
    latest: (workspaceId: string) => [...queryKeys.workspaceExports.all, "latest", workspaceId] as const,
  },

  featureOrders: {
    superuserList: ["superuser_featureOrderList"] as const,
  },

  upsellFeatures: {
    all: ["upsellFeatureList"] as const,
    lists: () => [...queryKeys.upsellFeatures.all, "list"] as const,
    list: (capability: string) => [...queryKeys.upsellFeatures.lists(), { capability }] as const,
    superuserList: ["superuser_upsellFeatureList"] as const,
  },

  entitlements: {
    all: ["entitlements"] as const,
    workspace: (workspaceId: string) => [...queryKeys.entitlements.all, workspaceId] as const,
  },

  offerings: {
    all: ["offerings"] as const,
    superuserList: ["superuser_offeringList"] as const,
    superuserCategoryList: ["superuser_offeringCategoryList"] as const,
    superuserInterestRequestList: ["superuser_offeringInterestRequestList"] as const,
    superuserInterestRequestNewCount: ["superuser_offeringInterestRequestNewCount"] as const,
    lists: () => [...queryKeys.offerings.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.offerings.lists(), { workspaceId }] as const,
    categoryLists: () => [...queryKeys.offerings.all, "categoryList"] as const,
    categoryList: (workspaceId: string) => [...queryKeys.offerings.categoryLists(), { workspaceId }] as const,
    details: () => [...queryKeys.offerings.all, "detail"] as const,
    detail: (slug: string, workspaceId: string) => [...queryKeys.offerings.details(), { slug, workspaceId }] as const,
  },

  teams: {
    all: ["teams"] as const,
    lists: () => [...queryKeys.teams.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.teams.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.teams.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.teams.details(), id] as const,
    membershipList: (teamId: string) => [...queryKeys.teams.all, "membershipList", teamId] as const,
    sharedList: (workspaceId: string) => [...queryKeys.teams.all, "sharedList", { workspaceId }] as const,
  },

  users: {
    all: ["users"] as const,
    lists: () => [...queryKeys.users.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.users.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.users.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    current: () => [...queryKeys.users.all, "current"] as const,
    superuserList: (includeAnonymized: boolean) =>
      [...queryKeys.users.all, "superuser_userList", { includeAnonymized }] as const,
    superuserAssignmentSummary: (userId: string) =>
      [...queryKeys.users.all, "superuser_userAssignmentSummary", { userId }] as const,
    superuserDetail: (userId: string) => [...queryKeys.users.all, "superuser_user", { userId }] as const,
    superuserSignInCapableCount: () =>
      [...queryKeys.users.all, "superuser_signInCapableSuperUserCount"] as const,
    superuserAccessOverview: (userId: string) =>
      [...queryKeys.users.all, "superuser_userAccessOverview", { userId }] as const,
    superuserAssignmentDetail: (userId: string, workspaceId: string) =>
      [...queryKeys.users.all, "superuser_userAssignmentDetail", { userId, workspaceId }] as const,
  },

  auth: {
    all: ["auth"] as const,
  },

  files: {
    all: ["files"] as const,
    lists: () => [...queryKeys.files.all, "list"] as const,
    list: (ownerId: string, ownerType: string) => [...queryKeys.files.lists(), { ownerId, ownerType }] as const,
    details: () => [...queryKeys.files.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.files.details(), id] as const,
  },

  assignees: {
    all: ["assignees"] as const,
    details: () => [...queryKeys.assignees.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.assignees.details(), id] as const,
    byOwner: (ownerId: string) => [...queryKeys.assignees.all, "byOwner", ownerId] as const,
  },

  projects: {
    all: ["projects"] as const,
    lists: () => [...queryKeys.projects.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.projects.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.projects.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.projects.details(), id] as const,
  },

  tasks: {
    all: ["tasks"] as const,
    lists: () => [...queryKeys.tasks.all, "list"] as const,
    list: (ownerId: string) => [...queryKeys.tasks.lists(), { ownerId }] as const,
    listForUser: (workspaceId: string) => [...queryKeys.tasks.lists(), "user", { workspaceId }] as const,
    details: () => [...queryKeys.tasks.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.tasks.details(), id] as const,
  },

  milestones: {
    all: ["milestones"] as const,
    lists: () => [...queryKeys.milestones.all, "list"] as const,
    listByOwner: (ownerId: string, ownerType: string) =>
      [...queryKeys.milestones.lists(), { ownerId, ownerType }] as const,
    details: () => [...queryKeys.milestones.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.milestones.details(), id] as const,
  },

  customFields: {
    all: ["customFields"] as const,
    lists: () => [...queryKeys.customFields.all, "list"] as const,
    listByOwner: (ownerId: string, ownerType: string) =>
      [...queryKeys.customFields.lists(), { ownerId, ownerType }] as const,
  },

  units: {
    all: ["units"] as const,
    lists: () => [...queryKeys.units.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.units.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.units.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.units.details(), id] as const,
  },

  focusAreas: {
    all: ["focusAreas"] as const,
    lists: () => [...queryKeys.focusAreas.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.focusAreas.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.focusAreas.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.focusAreas.details(), id] as const,
    instances: () => [...queryKeys.focusAreas.all, "instance"] as const,
    instance: (id: string) => [...queryKeys.focusAreas.instances(), id] as const,
    instanceByOwner: (ownerId: string) => [...queryKeys.focusAreas.instances(), "byOwner", { ownerId }] as const,
  },

  reductionPlans: {
    all: ["reductionPlans"] as const,
    lists: () => [...queryKeys.reductionPlans.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.reductionPlans.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.reductionPlans.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.reductionPlans.details(), id] as const,
    forClimateAction: (climateActionId: string) =>
      [...queryKeys.reductionPlans.all, "forClimateAction", { climateActionId }] as const,
  },

  scenarios: {
    all: ["scenarios"] as const,
    lists: () => [...queryKeys.scenarios.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.scenarios.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.scenarios.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.scenarios.details(), id] as const,
    transitionImpact: (id: string) => [...queryKeys.scenarios.detail(id), "transitionImpact"] as const,
    chartColorSlotConfigs: () => [...queryKeys.scenarios.all, "chartColorSlotConfig"] as const,
    chartColorSlotConfig: (id: string) => [...queryKeys.scenarios.chartColorSlotConfigs(), id] as const,
  },

  reductionLevers: {
    all: ["reductionLevers"] as const,
    lists: () => [...queryKeys.reductionLevers.all, "list"] as const,
    list: (scenarioModelId: string, scenarioId: string) =>
      [...queryKeys.reductionLevers.lists(), { scenarioModelId, scenarioId }] as const,
    unavailableList: (scenarioModelId: string) =>
      [...queryKeys.reductionLevers.all, "unavailableList", { scenarioModelId }] as const,
    powertrainMixBaseline: (scenarioModelId: string) =>
      [...queryKeys.reductionLevers.all, "powertrainMixBaseline", { scenarioModelId }] as const,
    modalSplitBaseline: (scenarioModelId: string) =>
      [...queryKeys.reductionLevers.all, "modalSplitBaseline", { scenarioModelId }] as const,
  },

  scenarioModels: {
    all: ["scenarioModels"] as const,
    lists: () => [...queryKeys.scenarioModels.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.scenarioModels.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.scenarioModels.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.scenarioModels.details(), id] as const,
  },

  transitions: {
    all: ["transitions"] as const,
    lists: () => [...queryKeys.transitions.all, "list"] as const,
    list: (scenarioModelId: string) => [...queryKeys.transitions.lists(), { scenarioModelId }] as const,
    details: () => [...queryKeys.transitions.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.transitions.details(), id] as const,
  },

  transitionConfigurations: {
    all: ["transitionConfigurations"] as const,
    lists: () => [...queryKeys.transitionConfigurations.all, "list"] as const,
    list: (transitionId: string) => [...queryKeys.transitionConfigurations.lists(), { transitionId }] as const,
    details: () => [...queryKeys.transitionConfigurations.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.transitionConfigurations.details(), id] as const,
  },

  labels: {
    all: ["labels"] as const,
    lists: () => [...queryKeys.labels.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.labels.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.labels.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.labels.details(), id] as const,
    instances: () => [...queryKeys.labels.all, "instance"] as const,
    instance: (id: string) => [...queryKeys.labels.instances(), id] as const,
    instanceLists: () => [...queryKeys.labels.all, "instanceList"] as const,
    instanceList: (ownerId: string) => [...queryKeys.labels.instanceLists(), { ownerId }] as const,
  },

  labelGroups: {
    all: ["labelGroups"] as const,
    lists: () => [...queryKeys.labelGroups.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.labelGroups.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.labelGroups.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.labelGroups.details(), id] as const,
  },

  priorities: {
    all: ["priorities"] as const,
    lists: () => [...queryKeys.priorities.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.priorities.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.priorities.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.priorities.details(), id] as const,
    instances: () => [...queryKeys.priorities.all, "instance"] as const,
    instance: (id: string) => [...queryKeys.priorities.instances(), id] as const,
    instanceByOwner: (ownerId: string) => [...queryKeys.priorities.instances(), "byOwner", { ownerId }] as const,
  },

  phases: {
    all: ["phases"] as const,
    lists: () => [...queryKeys.phases.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.phases.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.phases.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.phases.details(), id] as const,
    instances: () => [...queryKeys.phases.all, "instance"] as const,
    instance: (id: string) => [...queryKeys.phases.instances(), id] as const,
    instanceByOwner: (ownerId: string) => [...queryKeys.phases.instances(), "byOwner", { ownerId }] as const,
  },

  statuses: {
    all: ["statuses"] as const,
    lists: () => [...queryKeys.statuses.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.statuses.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.statuses.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.statuses.details(), id] as const,
    instances: () => [...queryKeys.statuses.all, "instance"] as const,
    instance: (id: string) => [...queryKeys.statuses.instances(), id] as const,
    instanceByOwner: (ownerId: string) => [...queryKeys.statuses.instances(), "byOwner", { ownerId }] as const,
  },

  methodologies: {
    all: ["methodologies"] as const,
    forSelect: () => [...queryKeys.methodologies.all, "for-select"] as const,
    details: () => [...queryKeys.methodologies.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.methodologies.details(), id] as const,
  },

  heatingSystemTypes: {
    all: ["heatingSystemTypes"] as const,
    list: () => [...queryKeys.heatingSystemTypes.all, "all"] as const,
    details: () => [...queryKeys.heatingSystemTypes.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.heatingSystemTypes.details(), id] as const,
  },

  streetLightings: {
    all: ["streetLightings"] as const,
    list: () => [...queryKeys.streetLightings.all, "all"] as const,
    details: () => [...queryKeys.streetLightings.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.streetLightings.details(), id] as const,
  },

  sectors: {
    all: ["sectors"] as const,
    details: () => [...queryKeys.sectors.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.sectors.details(), id] as const,
  },

  subsectors: {
    all: ["subsectors"] as const,
    details: () => [...queryKeys.subsectors.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.subsectors.details(), id] as const,
  },

  energySources: {
    all: ["energySources"] as const,
    details: () => [...queryKeys.energySources.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.energySources.details(), id] as const,
    byInventory: (inventoryId: string) => [...queryKeys.energySources.all, "byInventory", inventoryId] as const,
  },

  energySourceInstances: {
    all: ["energySourceInstances"] as const,
    details: () => [...queryKeys.energySourceInstances.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.energySourceInstances.details(), id] as const,
  },

  emissionFactors: {
    all: ["emissionFactors"] as const,
    details: () => [...queryKeys.emissionFactors.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.emissionFactors.details(), id] as const,
  },

  emissionFactorInstances: {
    all: ["emissionFactorInstances"] as const,
    details: () => [...queryKeys.emissionFactorInstances.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.emissionFactorInstances.details(), id] as const,
  },

  inventories: {
    all: ["inventories"] as const,
    lists: () => [...queryKeys.inventories.all, "list"] as const,
    list: (workspaceId: string, minimal?: boolean) =>
      [...queryKeys.inventories.lists(), { workspaceId, minimal: minimal ?? true }] as const,
    byIds: (workspaceId: string, inventoryIdList: string[]) =>
      [...queryKeys.inventories.lists(), "byIds", { workspaceId, inventoryIdList: [...inventoryIdList].sort() }] as const,
    forKspImport: (workspaceId: string, yearList: readonly number[]) =>
      [...queryKeys.inventories.lists(), "for-ksp-import", { workspaceId, yearList: [...yearList].sort((a, b) => a - b) }] as const,
    details: () => [...queryKeys.inventories.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.inventories.details(), id] as const,
    transportPrefillStatus: (id: string) => [...queryKeys.inventories.all, "transportPrefillStatus", id] as const,
    mileagePercentageListForYear: (year: number) =>
      [...queryKeys.inventories.all, "mileagePercentageListForYear", { year }] as const,
    specificConsumptionFactorsListForYear: (year: number) =>
      [...queryKeys.inventories.all, "specificConsumptionFactorsListForYear", { year }] as const,
    vehicleOccupancyRateList: (inventoryId: string, subsectorIdList: readonly string[], unit: string) =>
      [
        ...queryKeys.inventories.all,
        "vehicleOccupancyRateList",
        { inventoryId, subsectorIdList: [...subsectorIdList].sort(), unit },
      ] as const,
    mileageSubsector: (subsectorId: string) => [...queryKeys.inventories.all, "mileageSubsector", { subsectorId }] as const,
  },

  inventoryValues: {
    all: ["inventoryValues"] as const,
    details: () => [...queryKeys.inventoryValues.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.inventoryValues.details(), id] as const,
  },

  localInstallations: {
    all: ["localInstallations"] as const,
    lists: () => [...queryKeys.localInstallations.all, "list"] as const,
    list: (inventoryId: string) => [...queryKeys.localInstallations.lists(), { inventoryId }] as const,
    details: () => [...queryKeys.localInstallations.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.localInstallations.details(), id] as const,
  },

  storedViews: {
    all: ["storedViews"] as const,
    lists: () => [...queryKeys.storedViews.all, "list"] as const,
    list: (key: string, workspaceId: string) => [...queryKeys.storedViews.lists(), { key, workspaceId }] as const,
    details: () => [...queryKeys.storedViews.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.storedViews.details(), id] as const,
  },

  activeViews: {
    all: ["activeViews"] as const,
    lists: () => [...queryKeys.activeViews.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.activeViews.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.activeViews.all, "detail"] as const,
    detail: (key: string, workspaceId: string) => [...queryKeys.activeViews.details(), { key, workspaceId }] as const,
  },

  indicators: {
    all: ["indicators"] as const,
    lists: () => [...queryKeys.indicators.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.indicators.lists(), { workspaceId }] as const,
    listByOwner: (ownerId: string, ownerType: string) =>
      [...queryKeys.indicators.lists(), "byOwner", { ownerId, ownerType }] as const,
    details: () => [...queryKeys.indicators.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.indicators.details(), id] as const,
  },

  featureFlags: {
    all: ["featureFlags"] as const,
    lists: () => [...queryKeys.featureFlags.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.featureFlags.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.featureFlags.all, "detail"] as const,
    detail: (id: string, workspaceId: string) => [...queryKeys.featureFlags.details(), { id, workspaceId }] as const,
  },

  permissions: {
    all: ["permissions"] as const,
    check: (resource: string, action: string, resourceId: string) =>
      [...queryKeys.permissions.all, "check", { resource, action, resourceId }] as const,
    checkList: (permissions: Array<{ resource: string; action: string; resourceId: string }>) =>
      [...queryKeys.permissions.all, "checkList", permissions] as const,
  },

  sectorFilters: {
    all: ["sectorFilters"] as const,
    details: () => [...queryKeys.sectorFilters.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.sectorFilters.details(), id] as const,
  },

  trash: {
    all: ["trash"] as const,
    lists: () => [...queryKeys.trash.all, "list"] as const,
    workspaceLists: (workspaceId: string): readonly ["trash", "list", { readonly workspaceId: string }] =>
      [...queryKeys.trash.lists(), { workspaceId }] as const,
    list: (workspaceId: string, scope: string, entityType?: string, teamId?: string) =>
      [
        ...queryKeys.trash.lists(),
        { workspaceId, scope, entityType: entityType ?? null, teamId: teamId ?? null },
      ] as const,
  },
  publicDashboards: {
    all: ["publicDashboards"] as const,
    lists: () => [...queryKeys.publicDashboards.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.publicDashboards.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.publicDashboards.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.publicDashboards.details(), id] as const,
    withSections: (id: string) => [...queryKeys.publicDashboards.details(), id, "withSections"] as const,
    embed: (id: string) => [...queryKeys.publicDashboards.details(), id, "embed"] as const,
    themeSettings: (workspaceId: string) =>
      [...queryKeys.publicDashboards.all, "themeSettings", { workspaceId }] as const,
    climateActions: (dashboardId: string, teamId?: string) =>
      [...queryKeys.publicDashboards.all, "climateActions", { dashboardId, teamId }] as const,
    projects: (dashboardId: string, teamId?: string) =>
      [...queryKeys.publicDashboards.all, "projects", { dashboardId, teamId }] as const,
    focusAreas: (dashboardId: string) => [...queryKeys.publicDashboards.all, "focusAreas", { dashboardId }] as const,
    labels: (dashboardId: string) => [...queryKeys.publicDashboards.all, "labels", { dashboardId }] as const,
    labelGroups: (dashboardId: string) => [...queryKeys.publicDashboards.all, "labelGroups", { dashboardId }] as const,
    phases: (dashboardId: string) => [...queryKeys.publicDashboards.all, "phases", { dashboardId }] as const,
    priorities: (dashboardId: string) => [...queryKeys.publicDashboards.all, "priorities", { dashboardId }] as const,
    teams: (dashboardId: string) => [...queryKeys.publicDashboards.all, "teams", { dashboardId }] as const,
    workspaceMembers: (dashboardId: string) =>
      [...queryKeys.publicDashboards.all, "workspaceMembers", { dashboardId }] as const,
    duplicationProgress: (progressId: string) =>
      [...queryKeys.publicDashboards.all, "duplicationProgress", progressId] as const,
    trashLists: () => [...queryKeys.publicDashboards.all, "trash"] as const,
    trashList: (workspaceId: string) => [...queryKeys.publicDashboards.trashLists(), { workspaceId }] as const,
    forClimateAction: (climateActionId: string) =>
      [...queryKeys.publicDashboards.all, "forClimateAction", { climateActionId }] as const,
    forProject: (projectId: string) =>
      [...queryKeys.publicDashboards.all, "forProject", { projectId }] as const,
    defaultTemplateEmbed: (templateType: string, entityId: string | undefined, sourceDashboardId?: string) =>
      [...queryKeys.publicDashboards.all, "defaultTemplateEmbed", { templateType, entityId, sourceDashboardId }] as const,
    navConfig: (dashboardId: string) =>
      [...queryKeys.publicDashboards.all, "navConfig", { dashboardId }] as const,
    scenario: (dashboardId: string, scenarioId?: string) =>
      [...queryKeys.publicDashboards.all, "scenario", { dashboardId, scenarioId }] as const,
    scenarioInventory: (dashboardId: string, scenarioId?: string) =>
      [...queryKeys.publicDashboards.all, "scenarioInventory", { dashboardId, scenarioId }] as const,
    inventoriesByIds: (dashboardId: string, inventoryIds: string[]) =>
      [...queryKeys.publicDashboards.all, "inventories-by-ids", { dashboardId, inventoryIds }] as const,
    inventories: (dashboardId: string) =>
      [...queryKeys.publicDashboards.all, "inventories", { dashboardId }] as const,
    inventoriesMinimal: (dashboardId: string) =>
      [...queryKeys.publicDashboards.all, "inventories-minimal", { dashboardId }] as const,
    scenarioModels: (dashboardId: string) =>
      [...queryKeys.publicDashboards.all, "scenarioModels", { dashboardId }] as const,
    energySources: (dashboardId: string) =>
      [...queryKeys.publicDashboards.all, "energySources", { dashboardId }] as const,
    indicatorListForEntity: (dashboardId: string, entityId: string | undefined, entityType: string) =>
      [...queryKeys.publicDashboards.all, "indicatorListForEntity", { dashboardId, entityId, entityType }] as const,
  },

  dashboardExports: {
    all: ["dashboardExports"] as const,
    latest: (workspaceId: string, dashboardId: string) =>
      [...queryKeys.dashboardExports.all, "latest", { workspaceId, dashboardId }] as const,
    lists: (workspaceId: string) => [...queryKeys.dashboardExports.all, "list", { workspaceId }] as const,
    list: (workspaceId: string, page: number) =>
      [...queryKeys.dashboardExports.all, "list", { workspaceId, page }] as const,
    count: (workspaceId: string) => [...queryKeys.dashboardExports.all, "count", { workspaceId }] as const,
  },

  municipalities: {
    all: ["municipalities"] as const,
    lists: () => [...queryKeys.municipalities.all, "list"] as const,
    list: (search: string, take?: number) =>
      [...queryKeys.municipalities.all, "list", { search, ...(take !== undefined ? { take } : {}) }] as const,
    byIdList: (idList: string[]) =>
      [...queryKeys.municipalities.all, "byIdList", { idList: [...idList].sort() }] as const,
    mileageByYearAndMunicipality: (year: number, municipalityId: string) =>
      [...queryKeys.municipalities.all, "mileageByYearAndMunicipality", { year, municipalityId }] as const,
  },

  entityPermissions: {
    all: ["entityPermissions"] as const,
    lists: () => [...queryKeys.entityPermissions.all, "list"] as const,
    list: (entityId: string) => [...queryKeys.entityPermissions.lists(), { entityId }] as const,
    effectiveRight: (entityId: string, entityType: string, workspaceId: string) =>
      [...queryKeys.entityPermissions.all, "effectiveRight", { entityId, entityType, workspaceId }] as const,
    hiddenEntityCount: (entityType: string, workspaceId: string, contextEntityIdList: string[]) =>
      [...queryKeys.entityPermissions.all, "hiddenEntityCount", { entityType, workspaceId, contextEntityIdList }] as const,
  },

  dataRequests: {
    all: ["dataRequests"] as const,
    lists: () => [...queryKeys.dataRequests.all, "list"] as const,
    list: (workspaceId: string) => [...queryKeys.dataRequests.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.dataRequests.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.dataRequests.details(), id] as const,
    entity: (id: string) => [...queryKeys.dataRequests.all, "entity", id] as const,
  },

  adminAnalytics: {
    all: ["adminAnalytics"] as const,
    overview: () => [...queryKeys.adminAnalytics.all, "overview"] as const,
    loginsHeatmap: (weeks: number) => [...queryKeys.adminAnalytics.all, "loginsHeatmap", weeks] as const,
    activeWorkspaceUrlList: () => [...queryKeys.adminAnalytics.all, "activeWorkspaceUrlList"] as const,
    featureAdoption: (weeks: number) => [...queryKeys.adminAnalytics.all, "featureAdoption", weeks] as const,
    firstClimateActionUpdateList: () =>
      [...queryKeys.adminAnalytics.all, "firstClimateActionUpdateList"] as const,
    firstPageviewList: () => [...queryKeys.adminAnalytics.all, "firstPageviewList"] as const,
    analyticsWorkspaceList: () => [...queryKeys.adminAnalytics.all, "analyticsWorkspaceList"] as const,
    featureMatrixList: () => [...queryKeys.adminAnalytics.all, "featureMatrixList"] as const,
    workspaceSegmentList: () => [...queryKeys.adminAnalytics.all, "workspaceSegmentList"] as const,
    publicDashboardStats: (workspaceUrl: string) =>
      [...queryKeys.adminAnalytics.all, "publicDashboardStats", workspaceUrl] as const,
  },

  identityProvider: {
    all: ["identityProvider"] as const,
    superuserList: () => [...queryKeys.identityProvider.all, "superuserList"] as const,
    blockPreview: (revision: number) => [...queryKeys.identityProvider.all, "blockPreview", revision] as const,
  },

  fundingProgram: {
    all: ["fundingProgram"] as const,
    importRunList: (limit: number) => [...queryKeys.fundingProgram.all, "importRunList", limit] as const,
    importRun: (id: string) => [...queryKeys.fundingProgram.all, "importRun", id] as const,
    importSourceList: () => [...queryKeys.fundingProgram.all, "importSourceList"] as const,
    embeddingStats: () => [...queryKeys.fundingProgram.all, "embeddingStats"] as const,
    sourceSuggestionLists: () => [...queryKeys.fundingProgram.all, "sourceSuggestionList"] as const,
    sourceSuggestionList: (limit: number, status?: string) =>
      [...queryKeys.fundingProgram.sourceSuggestionLists(), limit, status ?? null] as const,
    exampleLists: () => [...queryKeys.fundingProgram.all, "exampleList"] as const,
    exampleList: (limit: number, status?: string) =>
      [...queryKeys.fundingProgram.exampleLists(), limit, status ?? null] as const,
    rssFeedList: () => [...queryKeys.fundingProgram.all, "rssFeedList"] as const,
    rssFeedItemList: (feedId: string) =>
      [...queryKeys.fundingProgram.all, "rssFeedItemList", feedId] as const,
  },

  fundingBatch: {
    all: ["fundingBatch"] as const,
    latestForWorkspace: (workspaceId: string) =>
      [...queryKeys.fundingBatch.all, "latestForWorkspace", { workspaceId }] as const,
  },

  fundingLinks: {
    all: ["fundingLinks"] as const,
    forClimateAction: (climateActionId: string, workspaceId: string) =>
      [...queryKeys.fundingLinks.all, "forClimateAction", climateActionId, workspaceId] as const,
    linkedActionsInWorkspaceBatch: (
      fundingProgramIds: string[],
      workspaceId: string,
      excludeClimateActionId?: string,
      filterProjectId?: string,
    ) =>
      [
        ...queryKeys.fundingLinks.all,
        "linkedActionsInWorkspaceBatch",
        [...fundingProgramIds].sort(),
        workspaceId,
        excludeClimateActionId ?? null,
        filterProjectId ?? null,
      ] as const,
    workspaceUsageBatch: (fundingProgramIds: string[], workspaceId: string) =>
      [...queryKeys.fundingLinks.all, "workspaceUsageBatch", [...fundingProgramIds].sort(), workspaceId] as const,
  },

  municipalProperties: {
    all: ["municipalProperties"] as const,
    histories: (propertyId: string): readonly ["municipalProperties", "history", string] =>
      [...queryKeys.municipalProperties.all, "history", propertyId] as const,
    history: ({
      propertyId,
      year,
    }: {
      propertyId: string;
      year: number;
    }): readonly ["municipalProperties", "history", string, number] =>
      [...queryKeys.municipalProperties.histories(propertyId), year] as const,
    usage: (propertyId: string): readonly ["municipalProperties", "usage", string] =>
      [...queryKeys.municipalProperties.all, "usage", propertyId] as const,
    lists: () => [...queryKeys.municipalProperties.all, "list"] as const,
    workspaceLists: (workspaceId: string): readonly ["municipalProperties", "list", string] =>
      [...queryKeys.municipalProperties.lists(), workspaceId] as const,
    inventoryCandidates: ({ workspaceId, inventoryId }: { workspaceId: string; inventoryId: string }) =>
      [...queryKeys.municipalProperties.workspaceLists(workspaceId), "inventory", inventoryId] as const,
    list: ({
      workspaceId,
      filter,
    }: {
      workspaceId: string;
      filter: { municipalityId: string | null; search: string; usage: string; lifecycle: string };
    }) => [...queryKeys.municipalProperties.workspaceLists(workspaceId), filter] as const,
  },

  municipalPropertyRegisters: {
    all: ["municipalPropertyRegisters"] as const,
    lists: () => [...queryKeys.municipalPropertyRegisters.all, "list"] as const,
    list: ({
      workspaceId,
      filter,
    }: {
      workspaceId: string;
      filter: { year: number | null };
    }) => [...queryKeys.municipalPropertyRegisters.lists(), workspaceId, filter] as const,
    details: () => [...queryKeys.municipalPropertyRegisters.all, "detail"] as const,
    detail: (registerId: string) => [...queryKeys.municipalPropertyRegisters.details(), registerId] as const,
  },

  municipalPropertyYears: {
    all: ["municipalPropertyYears"] as const,
    list: ({ registerId, inventoryId }: { registerId: string; inventoryId?: string }) =>
      [...queryKeys.municipalPropertyYears.all, "list", registerId, ...(inventoryId ? [{ inventoryId }] : [])] as const,
    contributions: ({ registerId, contribution, inventoryId }: { registerId: string; contribution: { subsectorId: string; energySourceId: string }; inventoryId: string }) =>
      [...queryKeys.municipalPropertyYears.list({ registerId, inventoryId }), contribution] as const,
    detail: ({ id, inventoryId }: { id: string; inventoryId?: string }) =>
      [...queryKeys.municipalPropertyYears.all, "detail", id, ...(inventoryId ? [{ inventoryId }] : [])] as const,
  },

  activityLogs: {
    all: ["activityLogs"] as const,
    lists: () => [...queryKeys.activityLogs.all, "list"] as const,
    list: (
      workspaceId: string,
      filters?: {
        entityType?: string;
        entityId?: string;
        ownerType?: string;
        ownerId?: string;
        startDate?: Date;
        endDate?: Date;
      },
    ) => [...queryKeys.activityLogs.lists(), workspaceId, filters || {}] as const,
    entityTypes: (workspaceId: string) => [...queryKeys.activityLogs.all, "entityTypes", workspaceId] as const,
    superuserList: (workspaceId: string) => [...queryKeys.activityLogs.all, "superuser", workspaceId] as const,
    canView: (workspaceId: string, entityType: string, entityId: string) =>
      [...queryKeys.activityLogs.all, "canView", workspaceId, entityType, entityId] as const,
    retentionPolicy: (workspaceId: string) =>
      [...queryKeys.activityLogs.all, "retentionPolicy", workspaceId] as const,
    purgePreviews: (workspaceId: string) => [...queryKeys.activityLogs.all, "purgePreview", workspaceId] as const,
    purgePreview: (workspaceId: string, olderThan?: Date) =>
      [...queryKeys.activityLogs.purgePreviews(workspaceId), olderThan?.toISOString() ?? null] as const,
  },
} as const;
